import React from "react";
import { Box, Button, Divider, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { useNavigate, useLocation, useOutletContext } from "react-router-dom";
import { PDFDownloadLink } from "@react-pdf/renderer";
import ContractPDF from "../../utils/ContractPDF";

const poppins = { fontFamily: "'Poppins', sans-serif" };

const InfoRow = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
    <Typography
      sx={{
        ...poppins,
        fontSize: "0.82rem",
        color: "rgba(15,31,69,0.55)",
      }}
    >
      {label}
    </Typography>

    <Typography
      sx={{
        ...poppins,
        fontSize: "0.82rem",
        fontWeight: 600,
        color: "#0f1f45",
        textAlign: "right",
        // maxWidth: "70%",
        wordBreak: "break-word",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const formatCouponRanges = (coupons) => {
  if (!coupons?.length) return "";

  const nums = [
    ...new Set(
      coupons
        .map((item) => parseInt(item.coupon, 10))
        .filter((num) => !Number.isNaN(num)),
    ),
  ].sort((a, b) => a - b);

  if (!nums.length) return "";

  const ranges = [];

  let start = nums[0];
  let end = nums[0];

  for (let i = 1; i < nums.length; i++) {
    if (nums[i] === end + 1) {
      end = nums[i];
      continue;
    }

    ranges.push(start === end ? `${start}` : `${start} - ${end}`);

    start = nums[i];
    end = nums[i];
  }

  ranges.push(start === end ? `${start}` : `${start} - ${end}`);

  return `${ranges.join(", ")} (${nums.length} kupon)`;
};

const getCouponText = (coupon, couponRange, couponText) => {
  if (couponText) return couponText;

  if (couponRange?.totalCoupon) {
    return `${couponRange.couponStart} - ${couponRange.couponEnd} (${couponRange.totalCoupon} kupon)`;
  }

  return formatCouponRanges(coupon);
};

export default function DealSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useOutletContext();

  const {
    transactionNo,
    contract,
    summary,
    coupon,
    couponRange,
    couponText,
    pdfFileName,
  } = location.state || {};

  const finalCouponText = getCouponText(coupon, couponRange, couponText);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 480,
        mx: "auto",
        px: 2,
        py: 4,
        zIndex: 1,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <img
        src="SolarTuffNatGath.png"
        alt="Strength of Loyalty & Relationships"
        style={{
          width: 80,
          objectFit: "contain",
          alignSelf: "flex-start",
        }}
      />

      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 140,
            height: 140,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255,255,255,0.25) 0%, transparent 70%)",
            filter: "blur(14px)",
          }}
        />

        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: "50%",
            border: "2px solid rgba(255,255,255,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.15)",
            boxShadow: "0 0 30px rgba(255,255,255,0.3)",
          }}
        >
          <CheckCircleIcon
            sx={{
              fontSize: 56,
              color: "#fff",
              filter: "drop-shadow(0 0 10px rgba(255,255,255,0.9))",
            }}
          />
        </Box>
      </Box>

      <Typography
        sx={{
          ...poppins,
          fontWeight: 700,
          fontSize: "1.6rem",
          color: "#fff",
          mb: 0.5,
          textAlign: "center",
        }}
      >
        Pemesanan Berhasil!
      </Typography>

      <Typography
        sx={{
          ...poppins,
          fontWeight: 400,
          fontSize: "0.85rem",
          color: "rgba(255,255,255,0.75)",
          mb: 3.5,
          textAlign: "center",
        }}
      >
        Pesanan Anda telah berhasil dibuat
      </Typography>

      <Box
        sx={{
          width: "100%",
          backgroundColor: "rgba(255,255,255,0.88)",
          backdropFilter: "blur(16px)",
          border: "1px solid rgba(255,255,255,0.95)",
          borderRadius: "16px",
          px: 2.5,
          py: 1,
          mb: 3,
        }}
      >
        <InfoRow label="No. Order" value={transactionNo || "-"} />

        <Divider sx={{ borderColor: "rgba(15,31,69,0.1)" }} />

        <InfoRow
          label="PDF Invoice"
          value={pdfFileName || `Invoice_${transactionNo}.pdf`}
        />

        <Divider sx={{ borderColor: "rgba(15,31,69,0.1)" }} />

        <InfoRow label="Kupon" value={finalCouponText || "-"} />
      </Box>

      {contract && (
        <PDFDownloadLink
          document={
            <ContractPDF
              contract={contract}
              summary={summary}
              coupon={coupon}
              couponRange={couponRange}
              couponText={finalCouponText}
              info={user[0]}
              no={transactionNo}
            />
          }
          fileName={`Invoice_${transactionNo}.pdf`}
          style={{
            textDecoration: "none",
            display: "block",
            width: "100%",
          }}
        >
          {({ loading: pdfLoading }) => (
            <Button
              variant="contained"
              fullWidth
              startIcon={<SaveAltIcon />}
              disabled={pdfLoading}
              sx={{
                ...poppins,
                background: "#1e3a6e",
                color: "#fff",
                borderRadius: "14px",
                py: 1.8,
                mb: 1.5,
                fontSize: "0.95rem",
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "0 6px 24px rgba(15,31,69,0.4)",
                "&:hover": { background: "#162d57" },
                "&.Mui-disabled": {
                  background: "rgba(255,255,255,0.4)",
                  color: "rgba(15,31,69,0.3)",
                },
              }}
            >
              {pdfLoading ? "Menyiapkan PDF..." : "Download PDF"}
            </Button>
          )}
        </PDFDownloadLink>
      )}

      <Typography
        onClick={() => navigate("/profile")}
        sx={{
          ...poppins,
          fontSize: "0.85rem",
          color: "rgba(255,255,255,0.8)",
          cursor: "pointer",
          textAlign: "center",
          textDecoration: "underline",
          "&:hover": { color: "#fff" },
        }}
      >
        Kembali ke Beranda
      </Typography>
    </Box>
  );
}
